import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import {
  Loader2,
  CheckCircle2,
  Clock,
  Users,
  FileCheck,
  BarChart3,
  Shield,
  Sparkles,
  Copy,
  ExternalLink,
  ArrowRight
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { PageTransition } from '@/components/PageTransition';

const DemoPage = () => {
  const navigate = useNavigate();
  usePageTitle('Try Demo');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const [demoCreated, setDemoCreated] = useState(false);
  const [demoCredentials, setDemoCredentials] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    companyName: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the terms to continue');
      return;
    }

    setIsLoading(true);

    try {
      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('utm_source') || urlParams.get('source') || 'direct';

      const { data, error: fnError } = await supabase.functions.invoke('create-demo-account', {
        body: {
          email: formData.email,
          firstName: formData.firstName,
          companyName: formData.companyName,
          source
        }
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to create demo account');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to create demo account');
      }

      // Store credentials and show success
      setDemoCredentials({
        email: data.demoEmail,
        password: data.password,
        expiresAt: data.expiresAt,
        loginUrl: data.loginUrl,
        isExisting: data.isExisting
      });
      setDemoCreated(true);

      if (data.emailSent) {
        toast.success('Credentials sent to your email!');
      }

    } catch (err) {
      console.error('Demo creation error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleAutoLogin = async () => {
    if (!demoCredentials?.email || !demoCredentials?.password) {
      // Fallback to manual login if no password (existing account)
      navigate('/login');
      return;
    }

    setIsLoggingIn(true);
    try {
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email: demoCredentials.email,
        password: demoCredentials.password
      });

      if (loginError) {
        console.error('Auto-login error:', loginError);
        toast.error('Auto-login failed. Please login manually.');
        navigate('/login');
        return;
      }

      toast.success('Welcome to your demo!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Auto-login error:', err);
      toast.error('Auto-login failed. Please login manually.');
      navigate('/login');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const features = [
    {
      icon: Users,
      title: '68 Team Members',
      description: 'Pre-populated team with realistic training data'
    },
    {
      icon: FileCheck,
      title: '105 Requirements',
      description: 'Complete compliance framework ready to explore'
    },
    {
      icon: BarChart3,
      title: '3 Spaces',
      description: 'Office, Warehouse, and Construction site examples'
    },
    {
      icon: Shield,
      title: 'Full Admin Access',
      description: 'Explore all features as an administrator'
    }
  ];

  // Success screen after demo creation
  if (demoCreated && demoCredentials) {
    return (
      <PageTransition>
        <div className="min-h-screen flex">
          {/* Left Panel - Success Animation */}
          <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 relative overflow-hidden items-center justify-center">
            <div className="relative z-10 text-center px-12">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm">
                <CheckCircle2 className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">
                {demoCredentials.isExisting ? 'Welcome Back!' : 'Demo Ready!'}
              </h1>
              <p className="text-xl text-white/90 max-w-md mx-auto">
                {demoCredentials.isExisting
                  ? 'Your existing demo account is still active.'
                  : 'Your demo environment has been created.'}
              </p>
              <div className="mt-8 flex items-center justify-center gap-2 text-white/80">
                <Clock className="w-5 h-5" />
                <span>Expires in 24 hours</span>
              </div>
            </div>

            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
            </div>
          </div>

          {/* Right Panel - Credentials */}
          <div className="flex-1 flex items-center justify-center p-8 bg-white">
            <div className="w-full max-w-md">
              <Card className="border-0 shadow-xl">
                <CardHeader className="text-center pb-4">
                  <div className="lg:hidden mb-6">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl font-bold text-slate-900">
                    Your Demo Credentials
                  </CardTitle>
                  <CardDescription className="text-slate-600 mt-2">
                    Use these credentials to log in and explore the platform
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Email credential */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Demo Email</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={demoCredentials.email}
                        readOnly
                        className="h-11 bg-slate-50 font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-11 w-11 shrink-0"
                        onClick={() => copyToClipboard(demoCredentials.email, 'Email')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Password credential */}
                  {demoCredentials.password && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Password</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={demoCredentials.password}
                          readOnly
                          className="h-11 bg-slate-50 font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-11 w-11 shrink-0"
                          onClick={() => copyToClipboard(demoCredentials.password, 'Password')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Important notice */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> This is a demo environment. Some features like sending emails
                      and invitations are disabled. Your demo expires in 24 hours.
                    </p>
                  </div>

                  {/* Login button */}
                  <Button
                    className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base"
                    onClick={handleAutoLogin}
                    disabled={isLoggingIn}
                  >
                    {isLoggingIn ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Logging you in...
                      </>
                    ) : (
                      <>
                        Start Exploring
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  {/* Email notice */}
                  <p className="text-center text-sm text-slate-500">
                    We've also emailed these credentials to {formData.email}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Initial form
  return (
    <PageTransition>
      <div className="min-h-screen flex">
        {/* Left Panel - Marketing */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
          <div className="relative z-10 flex flex-col w-full h-full justify-center px-12">
            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-6 h-6 text-amber-400" />
                <span className="text-amber-400 font-medium">Free 24-Hour Demo</span>
              </div>
              <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
                Experience the Future of<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
                  Training Compliance
                </span>
              </h1>
              <p className="text-xl text-gray-300 max-w-lg">
                Get instant access to a fully-populated demo environment.
                No credit card required. No commitment.
              </p>
            </div>

            {/* Feature grid */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10"
                >
                  <feature.icon className="w-8 h-8 text-blue-400 mb-3" />
                  <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Trust indicators */}
            <div className="mt-12 flex items-center gap-6 text-gray-400 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Secure & Private</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Setup in 30 seconds</span>
              </div>
            </div>
          </div>

          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-500/20 to-transparent rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-emerald-500/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md">
            <Card className="border-0 shadow-none">
              <CardHeader className="text-center pb-8">
                <div className="mb-6">
                  <img
                    src="https://storage.googleapis.com/trainingmatrix/images/tmlogoiconv2.png"
                    alt="Ai Skills Matrix"
                    className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-xl drop-shadow-md"
                  />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Start Your Free Demo
                </CardTitle>
                <CardDescription className="text-slate-600 mt-2">
                  Enter your details to get instant access
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                      Work Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="h-11"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="h-11"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyName" className="text-sm font-medium text-slate-700">
                      Company Name
                    </Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Acme Corp"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      className="h-11"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="flex items-start space-x-2 pt-2">
                    <Checkbox
                      id="terms"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      disabled={isLoading}
                    />
                    <Label
                      htmlFor="terms"
                      className="text-sm text-slate-600 cursor-pointer leading-relaxed"
                    >
                      I agree to receive marketing communications and accept the{' '}
                      <a href="/terms" className="text-slate-900 underline hover:no-underline">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" className="text-slate-900 underline hover:no-underline">
                        Privacy Policy
                      </a>
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-base"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Creating Your Demo...
                      </>
                    ) : (
                      <>
                        Get Instant Access
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Mobile feature highlights */}
                <div className="lg:hidden mt-8 pt-8 border-t">
                  <p className="text-sm text-slate-500 text-center mb-4">What's included:</p>
                  <div className="grid grid-cols-2 gap-3">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span>{feature.title}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Already have account */}
                <p className="text-center text-sm text-slate-600 mt-6">
                  Already have an account?{' '}
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 text-slate-900 hover:text-slate-700"
                    onClick={() => navigate('/login')}
                  >
                    Sign in
                  </Button>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default DemoPage;
